import { Fragment, useCallback, useEffect, useState } from 'react'
import { LuMenu } from 'react-icons/lu'
import { Outlet, useLocation } from 'react-router'
import { Tooltip } from 'react-tooltip'
import { IconButton } from '../components/ui/IconButton.tsx'
import { cn } from '../lib/cn.ts'
import { HINT_ID, hint } from '../lib/hint.ts'
import { useLiveUpdates } from '../lib/live.ts'
import { readStorage, writeStorage } from '../lib/storage.ts'
import { useMediaQuery } from '../lib/useMediaQuery.ts'
import { MODULES } from '../modules/registry.ts'
import { ThemeToggle } from '../theme/ThemeToggle.tsx'
import { useAutoTours } from '../tours/useTour.ts'
import { Brand } from './Brand.tsx'
import { LiveIndicator } from './LiveIndicator.tsx'
import { NAV_EVENTS } from './navEvents.ts'
import { SideNav } from './SideNav.tsx'
import { UserMenu } from './UserMenu.tsx'

const COLLAPSED_KEY = 'hrms.nav.collapsed'

export function AppShell() {
  const isMobile = useMediaQuery('(max-width: 767.98px)')
  // Touch-only devices: no hover hints (a tap would pop a tooltip over what you tapped).
  const noHover = useMediaQuery('(hover: none)')
  const [collapsed, setCollapsed] = useState(() => readStorage(COLLAPSED_KEY) === '1')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { pathname } = useLocation()

  useAutoTours()
  useLiveUpdates()

  const toggleNav = useCallback(() => {
    if (isMobile) setDrawerOpen((o) => !o)
    else
      setCollapsed((c) => {
        writeStorage(COLLAPSED_KEY, c ? '0' : '1')
        return !c
      })
  }, [isMobile])

  // Navigating closes the drawer and returns to the top.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on route change only
  useEffect(() => {
    setDrawerOpen(false)
    window.scrollTo({ top: 0 })
  }, [pathname])

  useEffect(() => {
    if (!isMobile) setDrawerOpen(false)
  }, [isMobile])

  useEffect(() => {
    const open = () => isMobile && setDrawerOpen(true)
    const close = () => setDrawerOpen(false)
    window.addEventListener(NAV_EVENTS.open, open)
    window.addEventListener(NAV_EVENTS.close, close)
    return () => {
      window.removeEventListener(NAV_EVENTS.open, open)
      window.removeEventListener(NAV_EVENTS.close, close)
    }
  }, [isMobile])

  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setDrawerOpen(false)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [drawerOpen])

  const navExpanded = isMobile ? drawerOpen : !collapsed
  const menuHint = isMobile
    ? drawerOpen
      ? 'Close menu'
      : 'Open menu'
    : collapsed
      ? 'Expand menu'
      : 'Collapse menu'

  return (
    <div className="min-h-dvh">
      <a
        href="#main"
        className="sr-only z-50 rounded-ctl bg-surface px-3 py-2 focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-40 border-b border-border bg-surface pt-[env(safe-area-inset-top)]">
        <div className="flex h-14 items-center gap-3 px-3 sm:gap-4 sm:px-5">
          <IconButton
            data-tour="hamburger"
            aria-label={menuHint}
            aria-expanded={navExpanded}
            aria-controls="app-nav"
            onClick={toggleNav}
            {...hint(menuHint)}
          >
            <LuMenu />
          </IconButton>
          <Brand />
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {MODULES.map(
              (m) =>
                m.TopBarAction && (
                  <Fragment key={m.id}>
                    <m.TopBarAction />
                  </Fragment>
                ),
            )}
            <LiveIndicator />
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>

      <div className="flex">
        {isMobile && drawerOpen && (
          <div
            aria-hidden
            className="fixed inset-x-0 top-[calc(56px+env(safe-area-inset-top))] bottom-0 z-30 animate-fade bg-scrim"
            onClick={() => setDrawerOpen(false)}
          />
        )}
        <aside
          id="app-nav"
          inert={isMobile && !drawerOpen}
          className={cn(
            'shrink-0 overflow-x-hidden overflow-y-auto border-r border-border bg-surface',
            isMobile
              ? cn(
                  'fixed top-[calc(56px+env(safe-area-inset-top))] bottom-0 left-0 z-30 w-[min(280px,85vw)] pb-[env(safe-area-inset-bottom)] transition-transform duration-200 ease-out',
                  drawerOpen ? 'translate-x-0 shadow-pop' : '-translate-x-full',
                )
              : cn(
                  'sticky top-14 h-[calc(100dvh-56px)] transition-[width] duration-200 ease-out',
                  collapsed ? 'w-16' : 'w-60',
                ),
          )}
        >
          <SideNav collapsed={!isMobile && collapsed} />
        </aside>

        <main id="main" tabIndex={-1} className="min-w-0 flex-1 outline-none">
          <div className="mx-auto w-full max-w-[1440px] px-5 pt-6 pb-16 max-[720px]:px-3.5 max-[720px]:pt-[18px] max-[720px]:pb-14">
            <Outlet />
          </div>
        </main>
      </div>

      <Tooltip
        id={HINT_ID}
        className="hint-tip"
        opacity={1}
        delayShow={300}
        delayHide={50}
        hidden={noHover}
        globalCloseEvents={{ escape: true, scroll: true }}
        arrowColor="var(--text)"
      />
    </div>
  )
}
